import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {BodyMediumBold} from '@spaced-out/ui-design-system/lib/components/Text';
import {Link} from '@spaced-out/ui-design-system/lib/components/Link';
import {useAsyncAction} from 'src/hooks/useAsyncAction';
import {SemanticIcon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {useCopyToClipboard} from '@spaced-out/ui-design-system/lib/hooks/useCopyToClipboard';
import css from './helpful-links.css';
import {useHistory} from 'src/rerouter';
import {
  Toast,
  ToastTitle,
  toastApi,
  TOAST_SEMANTIC,
  type ToastSemanticType,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import * as reduxApi from 'src/utils/redux-api-v2';
import {selectUserInfo, selectHasCrmPermission} from 'src/selectors/crm';
import {useDispatch, useSelector} from 'react-redux';
import {CRM_PERMISSIONS} from 'src/utils/crm-new/common';


const ORGANISATION_EMAIL = {
  adani: 'recruiter@adani.com',
  bata: 'career.in@bata.com',
} as const;

export const HelpfulLinks = () => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const dispatch = useDispatch();
  const router = useHistory();
  const userInfo = useSelector(selectUserInfo);
  const organization = userInfo?.organization ?? {};
  const {applicationDomain} = userInfo ?? {};
  const [copiedText, copy] = useCopyToClipboard();

  const DOWNLOAD_SURVEY_RESPONSE = selectHasCrmPermission(
    userInfo,
    CRM_PERMISSIONS.HIRING_MANAGER_SURVEY,
  );
  const INTERNAL_JOB_POSTING = selectHasCrmPermission(
    userInfo,
    CRM_PERMISSIONS.VIEW_IJP_REQUISITIONS,
  );
  const RESUME_MAIL_BOX_LINK = selectHasCrmPermission(
    userInfo,
    CRM_PERMISSIONS.VIEW_RESUME_MAILBOX,
  );

  const {isLoading: isDownloadingSurvey, act: handleDownloadSurvey} =
    useAsyncAction(async () => {
      try {
        toastApi.show(
          <Toast semantic={TOAST_SEMANTIC.information}>
            <ToastTitle>{`${t(
              'HM_Survey_Response_is_being_generating',
              'HM Survey Response is being generating',
            )}...`}</ToastTitle>
          </Toast>,
          {
            timeout: 2000,
          },
        );
        const response = await dispatch(
          reduxApi.get(
            '/api/export-hm-survey',
            {},
            {
              apiPath: '/trmapi',
            },
          ),
        );

        const blob = new Blob([response], {type: 'text/csv'});
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'survey_responses.csv');
        document.body?.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        toastApi.show(<SaveSuccessMessage />, {
          timeout: 2000,
        });
      } catch (err: any) {
        console.error('Error downloading survey:', err);
        toastApi.show(<ErrorDangerMessage />, {
          timeout: 2000,
        });
      }
    });
  const {code} = organization;

  const resumeMailBoxLink =
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly adani: "recruiter@adani.com"; readonly bata: "career.in@bata.com"; }'.
    ORGANISATION_EMAIL[code] ||
    `${code?.replaceAll('_', '-')}_candidate@resumebox.${applicationDomain}`;

  const showToastMessage = (message: string, type: ToastSemanticType) => {
    const toastJsx = (
      <Toast semantic={type}>
        <ToastTitle>{message} </ToastTitle>
      </Toast>
    );
    toastApi.show(toastJsx, {
      autoClose: true,
    });
  };

  const handleCopyResumeMailBoxLinkToClipboard = () => {
    copy(resumeMailBoxLink)
      .then((success) => {
        if (success) {
          showToastMessage(
            t('Link_copied_successfully', 'Link copied successfully'),
            TOAST_SEMANTIC.success,
          );
        } else {
          showToastMessage(
            t('Something_went_wrong', 'Something went wrong'),
            TOAST_SEMANTIC.danger,
          );
        }
      })
      .catch((_error) => {
        showToastMessage(
          t('Something_went_wrong', 'Something went wrong'),
          TOAST_SEMANTIC.danger,
        );
      });
  };
  const handleInternalJobPortal = () => {
    router.push('/trm#/internal-job-board');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className={css.cardTitleContainer}>
          <SemanticIcon
            name="handshake"
            semantic="neutral"
            type="solid"
            size="small"
          />
          {t('Helpful_Links', 'Helpful Links')}
        </CardTitle>
      </CardHeader>
      <CardContent className={css.cardContent}>
        {INTERNAL_JOB_POSTING && (
          <div className={css.cardItem}>
            <BodyMediumBold>
              {t(
                'View_Open_Jobs_for_Internal_Candidates',
                'View Open Jobs for Internal Candidates',
              )}{' '}
            </BodyMediumBold>
            <Link
              iconLeftName="arrow-right"
              underline={false}
              onClick={handleInternalJobPortal}
            >
              {t('Internal_Job_Posting', 'Internal Job Posting')}
            </Link>
          </div>
        )}
        {DOWNLOAD_SURVEY_RESPONSE && (
          <div className={css.cardItem}>
            <BodyMediumBold>{`${t(
              'Download_Hiring_Manager_experience_survey_responses',
              'Download Hiring Manager experience survey responses',
            )}.`}</BodyMediumBold>
            <Link
              iconLeftName="download"
              onClick={handleDownloadSurvey}
              underline={false}
            >
              {t('Download_Survey_Responses', 'Download Survey Responses')}
            </Link>
          </div>
        )}
        {RESUME_MAIL_BOX_LINK && (
          <div className={css.cardItem}>
            <BodyMediumBold>
              {`${t(
                'Directly_add_candidates_by_sending_their_resumes_to_this_Email_ID',
                'Directly add candidates by sending their resumes to this Email ID',
              )}:`}{' '}
            </BodyMediumBold>
            <Link
              iconLeftName="clipboard"
              onClick={handleCopyResumeMailBoxLinkToClipboard}
              underline={false}
            >
              {resumeMailBoxLink}
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const SaveSuccessMessage = () => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  return (
    <Toast semantic={TOAST_SEMANTIC.success}>
      <ToastTitle>{`${t(
        'Survey_Responses_downloaded_successfully',
        'Survey Responses downloaded successfully',
      )}!`}</ToastTitle>
    </Toast>
  );
};

export const ErrorDangerMessage = () => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  return (
    <Toast semantic={TOAST_SEMANTIC.danger}>
      <ToastTitle>{`${t(
        'Unable_to_download_Survey_Responses',
        'Unable to download Survey Responses',
      )}`}</ToastTitle>
    </Toast>
  );
};
